import { useState } from "react";
import { FaHome } from "react-icons/fa";
import { goLiveLabel } from "../../utils/goLive";
import {
  formatAddress,
  formatAgentName,
  formatCityLine,
  formatMeta,
  formatOpenHouse,
  formatPrice,
  formatSpecs,
} from "./listingDisplay";

// Photos are the point of this view, so the first one runs the full width of the
// card. A listing with none still has to look deliberate rather than broken.
const Photo = ({ photos = [], badge }) => {
  const [failed, setFailed] = useState(false);
  const src = photos[0];

  return (
    <div className="relative h-40 w-full overflow-hidden bg-light_gray">
      {src && !failed ? (
        <img
          alt=""
          src={src}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <FaHome color="#c7c9d3" size={34} />
        </div>
      )}

      {badge && (
        <span className="absolute left-2 top-2 rounded-sm bg-blue px-2 py-0.5 text-[10px] font-extrabold tracking-wide text-white">
          {badge}
        </span>
      )}

      {photos.length > 0 && (
        <span className="absolute bottom-2 right-2 rounded-sm bg-dark_gray/85 px-1.5 py-0.5 text-[10px] font-bold text-white">
          {photos.length} {photos.length === 1 ? "photo" : "photos"}
        </span>
      )}
    </div>
  );
};

const UpcomingListingCard = ({ listing, onSelect }) => {
  const address = formatAddress(listing);
  const cityLine = formatCityLine(listing);
  const price = formatPrice(listing.price);
  const specs = formatSpecs(listing);
  const meta = formatMeta(listing);
  const agent = formatAgentName(listing.user);
  // The card shows the next open house only; the detail view lists them all.
  const openHouse = formatOpenHouse(listing.openHouses?.[0]);

  // Until the detail view exists the card is still a card, just not a control —
  // rendering a button that goes nowhere would be worse than rendering none.
  const Wrapper = onSelect ? "button" : "div";
  const interactive = onSelect
    ? "text-left transition-all duration-150 hover:border-primary hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-primary"
    : "";

  return (
    <Wrapper
      {...(onSelect && { type: "button", onClick: () => onSelect(listing) })}
      className={`flex w-full flex-col overflow-hidden rounded-lg border border-light_gray bg-white shadow-card ${interactive}`}
    >
      <Photo photos={listing.imageURLs ?? []} badge={goLiveLabel(listing.expiration)} />

      <div className="flex flex-1 flex-col p-3">
        {address && (
          <div className="text-sm font-extrabold text-dark_gray">{address}</div>
        )}
        {cityLine && <div className="text-xs text-gray-400">{cityLine}</div>}
        {price && (
          <div className="mt-1 text-lg font-black text-primary_dark">{price}</div>
        )}
        {specs && <div className="text-xs text-secondary">{specs}</div>}
        {meta && <div className="mt-0.5 text-[11px] text-gray-400">{meta}</div>}

        {openHouse && (
          <span className="mt-2 mb-3 self-start rounded-sm border border-[#cfe0ef] bg-[#f2f8fc] px-1.5 py-0.5 text-[10px] font-extrabold text-blue">
            Open House · {openHouse}
          </span>
        )}

        {agent && (
          <div className="mt-auto flex items-center gap-2 border-t border-light_gray pt-3 text-xs text-gray-400">
            <span className="h-4 w-4 shrink-0 rounded-full bg-light_green" />
            {agent}
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default UpcomingListingCard;
